import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const API_DOCUMENTO = "/api/auditoria/documento";
const API_REVISION = "/api/auditoria/revision";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const MESES = [
  "",
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const nombrePersona = (persona) =>
  [
    persona?.nombre1Instructor,
    persona?.nombre2Instructor,
    persona?.apellido1Instructor,
    persona?.apellido2Instructor,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim() || "—";

const formatearFecha = (valor) => {
  if (!valor) return "No registrado";

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return "No registrado";
  }

  return fecha.toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatearFechaHora = (valor) => {
  if (!valor) return "No registrado";

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return "No registrado";
  }

  return fecha.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const nombrePeriodo = (mes, anio) => {
  if (!mes && !anio) return "No registrado";

  const nombreMes = MESES[Number(mes)] || mes || "";

  return `${nombreMes}${anio ? ` ${anio}` : ""}`.trim();
};

const obtenerTextoObligacion = (obligacion, index) => {
  const base = obligacion?.obligacionid || {};

  return (
    base?.obligacion ||
    base?.descripcionObligacion ||
    base?.nombreObligacion ||
    base?.descripcion ||
    `Obligación ${index + 1}`
  );
};

const Auditoria_RevisarDocumento = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [expediente, setExpediente] = useState(null);
  const [documento, setDocumento] = useState(null);
  const [criterios, setCriterios] = useState([]);
  const [revisiones, setRevisiones] = useState([]);
  const [detalles, setDetalles] = useState({});

  const [resultadoRevision, setResultadoRevision] = useState("");
  const [conceptoAuditor, setConceptoAuditor] = useState("");
  const [observacionRevision, setObservacionRevision] = useState("");

  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const colores = {
    fondo:
      "linear-gradient(135deg, #0e1319 0%, #212529 40%, #0d6efd 100%)",
    azul: "#0d6efd",
    verde: "#198754",
    amarillo: "#ffc107",
    rojo: "#dc3545",
    gris: "#6c757d",
  };

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");

        Swal.fire(
          "Sesión expirada",
          "Inicia sesión nuevamente.",
          "warning"
        ).then(() => navigate("/login"));

        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          mensaje ||
          "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargar = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        `${API_DOCUMENTO}/expediente/${id}`,
        authConfig()
      );

      setExpediente(data?.auditoriaDocumento || null);
      setDocumento(data?.documento || null);

      const criteriosRecibidos = Array.isArray(data?.criterios)
        ? data.criterios
        : [];

      setCriterios(criteriosRecibidos);

      setRevisiones(
        Array.isArray(data?.revisiones)
          ? data.revisiones
          : []
      );

      const base = {};

      criteriosRecibidos.forEach((criterio) => {
        base[criterio._id] = {
          criterioObjectId: criterio._id,
          estadoCriterioRevision: "",
          observacionCriterioRevision: "",
        };
      });

      setDetalles(base);
      setResultadoRevision("");
      setConceptoAuditor("");
      setObservacionRevision("");
    } catch (error) {
      manejarError(
        error,
        "No se pudo cargar el expediente de auditoría."
      );
    } finally {
      setLoading(false);
    }
  }, [id, manejarError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const puedeRegistrarRevision =
    expediente?.estadoAuditoria === "En revisión";

  const cambiarDetalle = useCallback(
    (criterioId, campo, valor) => {
      setDetalles((prev) => ({
        ...prev,
        [criterioId]: {
          ...(prev[criterioId] || {
            criterioObjectId: criterioId,
          }),
          [campo]: valor,
        },
      }));
    },
    []
  );

  const datosInforme = documento?.documento || {};
  const contrato = datosInforme?.contratoId || {};

  const instructor =
    expediente?.instructorObjectId ||
    documento?.instructor ||
    {};

  const auditor = expediente?.auditorObjectId || {};
  const resumen = documento?.resumenDocumento || {};

  const obligaciones = useMemo(
    () =>
      Array.isArray(datosInforme?.obligaciones)
        ? datosInforme.obligaciones
        : [],
    [datosInforme]
  );

  const desplazamientos = useMemo(
    () =>
      Array.isArray(datosInforme?.desplazamientos)
        ? datosInforme.desplazamientos
        : [],
    [datosInforme]
  );

  const estadisticas = useMemo(() => {
    const total = criterios.length;

    const evaluados = criterios.filter(
      (criterio) =>
        Boolean(
          detalles[criterio._id]
            ?.estadoCriterioRevision
        )
    ).length;

    const correctos = criterios.filter(
      (criterio) =>
        detalles[criterio._id]
          ?.estadoCriterioRevision === "Correcto"
    ).length;

    const ajustes = criterios.filter(
      (criterio) =>
        detalles[criterio._id]
          ?.estadoCriterioRevision ===
        "Requiere ajuste"
    ).length;

    const noAplica = criterios.filter(
      (criterio) =>
        detalles[criterio._id]
          ?.estadoCriterioRevision === "No aplica"
    ).length;

    return {
      total,
      evaluados,
      correctos,
      ajustes,
      noAplica,
      porcentaje:
        total > 0
          ? Math.round((evaluados / total) * 100)
          : 0,
    };
  }, [criterios, detalles]);

  const obtenerEstiloEstado = (estado) => {
    const valor = String(estado || "")
      .trim()
      .toUpperCase();

    if (valor === "APROBADO" || valor === "CORRECTO") {
      return {
        color: colores.verde,
        clase: "bg-success text-white",
        icono: "fa-solid fa-circle-check",
      };
    }

    if (
      valor === "EN REVISIÓN" ||
      valor === "EN REVISION"
    ) {
      return {
        color: colores.azul,
        clase: "bg-primary text-white",
        icono: "fa-solid fa-magnifying-glass",
      };
    }

    if (
      valor === "REQUIERE AJUSTES" ||
      valor === "REQUIERE AJUSTE"
    ) {
      return {
        color: colores.amarillo,
        clase: "bg-warning text-dark",
        icono: "fa-solid fa-triangle-exclamation",
      };
    }

    if (valor === "RECHAZADO") {
      return {
        color: colores.rojo,
        clase: "bg-danger text-white",
        icono: "fa-solid fa-circle-xmark",
      };
    }

    return {
      color: colores.gris,
      clase: "bg-secondary text-white",
      icono: "fa-solid fa-circle-info",
    };
  };

  const irASeccion = (seccionId) => {
    document.getElementById(seccionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const guardarRevision = useCallback(
    async (e) => {
      e.preventDefault();

      if (!puedeRegistrarRevision) {
        Swal.fire(
          "Atención",
          "Este expediente no se encuentra actualmente en revisión.",
          "warning"
        );
        return;
      }

      if (!resultadoRevision) {
        Swal.fire(
          "Atención",
          "Seleccione el resultado final de la revisión.",
          "warning"
        );
        return;
      }

      const detallesArray = criterios.map(
        (criterio) => ({
          criterioObjectId: criterio._id,
          estadoCriterioRevision:
            detalles[criterio._id]
              ?.estadoCriterioRevision || "",
          observacionCriterioRevision:
            detalles[criterio._id]
              ?.observacionCriterioRevision || "",
        })
      );

      const faltantes = criterios.filter(
        (criterio) =>
          criterio.obligatorioCriterio !== false &&
          !detalles[criterio._id]
            ?.estadoCriterioRevision
      );

      if (faltantes.length > 0) {
        Swal.fire(
          "Criterios pendientes",
          `Debe evaluar: ${faltantes
            .map(
              (criterio) =>
                criterio.nombreCriterio
            )
            .join(", ")}.`,
          "warning"
        );
        return;
      }

      if (
        resultadoRevision === "Aprobado" &&
        detallesArray.some(
          (detalle) =>
            detalle.estadoCriterioRevision ===
            "Requiere ajuste"
        )
      ) {
        Swal.fire(
          "Atención",
          "No puede aprobar mientras existan criterios que requieren ajuste.",
          "warning"
        );
        return;
      }

      try {
        setGuardando(true);

        await axios.post(
          API_REVISION,
          {
            auditoriaDocumentoObjectId:
              expediente._id,
            resultadoRevision,
            conceptoAuditor,
            observacionRevision,
            detalles: detallesArray,
          },
          authConfig()
        );

        await Swal.fire({
          icon: "success",
          title: "Revisión registrada",
          text: "El resultado quedó guardado en el historial de auditoría.",
          timer: 1800,
          showConfirmButton: false,
        });

        await cargar();
      } catch (error) {
        manejarError(
          error,
          "No se pudo registrar la revisión."
        );
      } finally {
        setGuardando(false);
      }
    },
    [
      puedeRegistrarRevision,
      resultadoRevision,
      criterios,
      detalles,
      expediente,
      conceptoAuditor,
      observacionRevision,
      cargar,
      manejarError,
    ]
  );

  if (loading) {
    return (
      <main className="min-vh-100 d-flex align-items-center justify-content-center bg-body-tertiary">
        <section className="text-center">
          <div className="spinner-border text-dark"></div>

          <p className="mt-3 text-secondary mb-0">
            Cargando expediente de auditoría...
          </p>
        </section>
      </main>
    );
  }

  if (!expediente || !documento) {
    return (
      <main className="container py-5">
        <section className="alert alert-warning text-center rounded-4">
          No fue posible cargar el expediente de auditoría.
        </section>

        <div className="text-center">
          <button
            type="button"
            className="btn btn-dark mt-3"
            onClick={() =>
              navigate("/auditoria/documento")
            }
          >
            <i className="fa-solid fa-arrow-left me-2"></i>
            Volver al panel
          </button>
        </div>
      </main>
    );
  }

  const estiloEstadoPrincipal =
    obtenerEstiloEstado(expediente.estadoAuditoria);

  return (
    <main className="container-fluid min-vh-90">
      {/* =========================================================
          ENCABEZADO PRINCIPAL
      ========================================================= */}
      <section
        className="rounded-4 shadow-sm p-4 mb-3 text-white"
        style={{ background: colores.fondo }}
      >
        <div className="row align-items-center text-center text-lg-start">
          <div className="col-lg-5">
            <div className="small text-uppercase opacity-75 fw-semibold mb-1">
              Auditoría documental
            </div>

            <h2 className="fw-bold mb-1">
              Revisión documental
            </h2>

            <p className="mb-2 opacity-75">
              {expediente.tipoDocumentoObjectId
                ?.nombreTipoDocumento ||
                documento.codigoTipoDocumento}
            </p>

            <span
              className={`badge rounded-pill px-3 py-2 ${estiloEstadoPrincipal.clase}`}
            >
              <i
                className={`${estiloEstadoPrincipal.icono} me-2`}
              ></i>
              {expediente.estadoAuditoria}
            </span>
          </div>

          <div className="col-lg-7 mt-4 mt-lg-0">
            <div className="row g-2 justify-content-center justify-content-lg-end">
              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={cargar}
                >
                  <i className="fa-solid fa-rotate d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Actualizar
                  </small>
                </button>
              </div>

              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={() =>
                    irASeccion("documentoAuditado")
                  }
                >
                  <i className="fa-regular fa-file-lines d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Documento
                  </small>
                </button>
              </div>

              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={() =>
                    irASeccion("criteriosAuditoria")
                  }
                >
                  <i className="fa-solid fa-list-check d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Criterios
                  </small>
                </button>
              </div>

              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <button
                  type="button"
                  className="btn btn-light rounded-4 w-100 py-3 text-center shadow-sm h-100"
                  onClick={() =>
                    navigate("/auditoria/documento")
                  }
                >
                  <i className="fa-solid fa-arrow-left d-block fs-4 mb-2"></i>
                  <small className="fw-semibold">
                    Panel
                  </small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          TARJETAS RESUMEN
      ========================================================= */}
      <section className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-solid fa-user"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Instructor
                </div>

                <div className="fw-bold fs-6">
                  {nombrePersona(instructor)}
                </div>

                <small className="text-secondary">
                  {instructor.documentoInstructor ||
                    "Sin documento"}
                </small>
              </div>
            </div>
          </article>
        </div>

        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-regular fa-calendar"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Periodo
                </div>

                <div className="fw-bold fs-6">
                  {nombrePeriodo(
                    expediente.periodoMes,
                    expediente.periodoAnio
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>

        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-solid fa-user-shield"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Auditor
                </div>

                <div className="fw-bold fs-6">
                  {nombrePersona(auditor)}
                </div>

                <small className="text-secondary">
                  {auditor?.rolObjectId?.nombreRol ||
                    "Sin rol"}
                </small>
              </div>
            </div>
          </article>
        </div>

        <div className="col-6 col-md-3">
          <article className="bg-white border rounded-4 shadow-sm p-3 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center text-center text-md-start gap-3">
              <div
                className="rounded-4 text-white d-flex align-items-center justify-content-center"
                style={{
                  width: 52,
                  height: 52,
                  background: colores.fondo,
                }}
              >
                <i className="fa-solid fa-clock-rotate-left"></i>
              </div>

              <div>
                <div className="small text-secondary">
                  Revisiones
                </div>

                <div className="fw-bold fs-6">
                  {revisiones.length}
                </div>

                <small className="text-secondary">
                  Historial registrado
                </small>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* =========================================================
          DOCUMENTO + RESUMEN DE ESTADO
      ========================================================= */}
      <section className="row g-4 mb-4">
        <aside className="col-12 col-xl-4">
          <article className="bg-white border rounded-4 shadow-sm overflow-hidden h-100">
            <div className="p-4 text-center border-bottom">
              <div
                className="mx-auto rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                style={{
                  width: 120,
                  height: 120,
                  backgroundColor:
                    estiloEstadoPrincipal.color,
                  border: "5px solid #f8f9fa",
                }}
              >
                <i
                  className={`${estiloEstadoPrincipal.icono} fs-1`}
                ></i>
              </div>

              <h4 className="fw-bold mt-3 mb-2">
                {expediente.estadoAuditoria}
              </h4>

              <span className="badge rounded-pill bg-light text-dark border px-3 py-2">
                Expediente de auditoría
              </span>
            </div>

            <div className="p-3 p-md-4">
              <div className="row g-3">
                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="small text-secondary">
                      Criterios
                    </div>
                    <div className="fw-bold text-dark fs-5">
                      {estadisticas.total}
                    </div>
                  </div>
                </div>

                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="small text-secondary">
                      Evaluados
                    </div>
                    <div className="fw-bold text-dark fs-5">
                      {estadisticas.evaluados}
                    </div>
                  </div>
                </div>

                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="small text-secondary">
                      Correctos
                    </div>
                    <div className="fw-bold text-success fs-5">
                      {estadisticas.correctos}
                    </div>
                  </div>
                </div>

                <div className="col-6">
                  <div className="border rounded-4 bg-light p-3 h-100">
                    <div className="small text-secondary">
                      Ajustes
                    </div>
                    <div className="fw-bold text-warning fs-5">
                      {estadisticas.ajustes}
                    </div>
                  </div>
                </div>
              </div>

              {criterios.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between small mb-2">
                    <span className="text-secondary">
                      Avance de revisión
                    </span>
                    <span className="fw-bold">
                      {estadisticas.porcentaje}%
                    </span>
                  </div>

                  <div
                    className="progress"
                    style={{ height: 8 }}
                  >
                    <div
                      className="progress-bar"
                      role="progressbar"
                      style={{
                        width: `${estadisticas.porcentaje}%`,
                      }}
                      aria-valuenow={
                        estadisticas.porcentaje
                      }
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </article>
        </aside>

        <section
          id="documentoAuditado"
          className="col-12 col-xl-8"
        >
          <article className="bg-white border rounded-4 shadow-sm p-3 p-md-4 h-100">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 mb-3">
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-center justify-content-md-start gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: 38,
                    height: 38,
                    backgroundColor: colores.azul,
                  }}
                >
                  <i className="fa-regular fa-file-lines"></i>
                </div>

                <div className="text-center text-md-start">
                  <h5
                    className="fw-bold mb-0"
                    style={{
                      color: colores.azul,
                    }}
                  >
                    Documento consultado
                  </h5>

                  <small className="text-secondary">
                    Información original del informe contractual
                  </small>
                </div>
              </div>

              <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                {nombrePeriodo(
                  expediente.periodoMes,
                  expediente.periodoAnio
                )}
              </span>
            </div>

            <hr />

            <div className="row g-3 mb-4">
              <div className="col-6 col-md-4">
                <div className="border rounded-4 bg-light p-3 h-100">
                  <div className="small text-secondary">
                    Fecha del informe
                  </div>
                  <div className="fw-bold">
                    {formatearFecha(
                      resumen.fechaInforme
                    )}
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 bg-light p-3 h-100">
                  <div className="small text-secondary">
                    Ciudad
                  </div>
                  <div className="fw-bold">
                    {resumen.ciudad || "No registrado"}
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 bg-light p-3 h-100">
                  <div className="small text-secondary">
                    Planilla
                  </div>
                  <div className="fw-bold">
                    {resumen.numeroPlanilla ||
                      "No registrado"}
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 bg-light p-3 h-100">
                  <div className="small text-secondary">
                    Periodo planilla
                  </div>
                  <div className="fw-bold">
                    {resumen.periodoPlanilla ||
                      "No registrado"}
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 bg-light p-3 h-100">
                  <div className="small text-secondary">
                    Supervisor
                  </div>
                  <div className="fw-bold">
                    {contrato?.supervisor?.nombre ||
                      "No registrado"}
                  </div>
                </div>
              </div>

              <div className="col-6 col-md-4">
                <div className="border rounded-4 bg-light p-3 h-100">
                  <div className="small text-secondary">
                    Centro
                  </div>
                  <div className="fw-bold">
                    {contrato?.centroFormacion
                      ?.nombreCentro ||
                      "No registrado"}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h6 className="fw-bold mb-0">
                  Obligaciones, actividades y evidencias
                </h6>
                <small className="text-secondary">
                  Información contractual auditada
                </small>
              </div>

              <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                {obligaciones.length} obligación
                {obligaciones.length === 1 ? "" : "es"}
              </span>
            </div>

            {obligaciones.length === 0 ? (
              <div className="text-center py-4 border rounded-4 bg-light">
                <i className="fa-regular fa-folder-open fs-2 text-secondary"></i>
                <div className="fw-semibold mt-2">
                  Sin obligaciones registradas
                </div>
              </div>
            ) : (
              <div className="table-responsive border rounded-4 overflow-hidden">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="px-3 py-3">#</th>
                      <th className="py-3">
                        Obligación
                      </th>
                      <th className="py-3">
                        Actividad / Acción
                      </th>
                      <th className="px-3 py-3">
                        Evidencia
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {obligaciones.map(
                      (obligacion, index) => (
                        <tr
                          key={
                            obligacion._id ||
                            index
                          }
                        >
                          <td className="px-3">
                            <span className="badge rounded-pill bg-light text-dark border">
                              {index + 1}
                            </span>
                          </td>

                          <td className="fw-semibold">
                            {obtenerTextoObligacion(
                              obligacion,
                              index
                            )}
                          </td>

                          <td>
                            {obligacion.accion ||
                              "—"}
                          </td>

                          <td className="px-3">
                            {obligacion.evidencia ||
                              "—"}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {desplazamientos.length > 0 && (
              <div className="mt-4">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                  <div>
                    <h6 className="fw-bold mb-0">
                      Desplazamientos
                    </h6>
                    <small className="text-secondary">
                      Registros relacionados con el informe
                    </small>
                  </div>

                  <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                    {desplazamientos.length} registro
                    {desplazamientos.length === 1
                      ? ""
                      : "s"}
                  </span>
                </div>

                <div className="table-responsive border rounded-4 overflow-hidden">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="px-3 py-3">
                          Orden
                        </th>
                        <th className="py-3">
                          Lugar
                        </th>
                        <th className="py-3">
                          Inicio
                        </th>
                        <th className="px-3 py-3">
                          Final
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {desplazamientos.map(
                        (
                          desplazamiento,
                          index
                        ) => (
                          <tr
                            key={
                              desplazamiento._id ||
                              index
                            }
                          >
                            <td className="px-3">
                              {desplazamiento
                                .ordenViajeNo ||
                                desplazamiento.orden ||
                                "—"}
                            </td>

                            <td>
                              {desplazamiento.lugar ||
                                "—"}
                            </td>

                            <td>
                              {formatearFecha(
                                desplazamiento.fechaInicio
                              )}
                            </td>

                            <td className="px-3">
                              {formatearFecha(
                                desplazamiento.fechaFinal
                              )}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </article>
        </section>
      </section>

      {/* =========================================================
          CRITERIOS
      ========================================================= */}
      <form onSubmit={guardarRevision}>
        <article
          id="criteriosAuditoria"
          className="bg-white border rounded-4 shadow-sm overflow-hidden mb-4"
        >
          <div className="p-3 p-md-4">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
              <div className="d-flex flex-column flex-md-row align-items-center gap-2 text-center text-md-start">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: 38,
                    height: 38,
                    backgroundColor: colores.azul,
                  }}
                >
                  <i className="fa-solid fa-list-check"></i>
                </div>

                <div>
                  <h5
                    className="fw-bold mb-0"
                    style={{
                      color: colores.azul,
                    }}
                  >
                    Criterios de auditoría
                  </h5>

                  <small className="text-secondary">
                    Evalúa cada criterio del documento
                  </small>
                </div>
              </div>

              <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                {estadisticas.evaluados} de{" "}
                {estadisticas.total} evaluados
              </span>
            </div>
          </div>

          {criterios.length === 0 ? (
            <div className="text-center py-5 border-top">
              <i className="fa-solid fa-list-check fs-1 text-secondary"></i>

              <h5 className="mt-3">
                Sin criterios configurados
              </h5>

              <p className="text-secondary mb-0">
                Este tipo documental aún no tiene criterios activos.
              </p>
            </div>
          ) : (
            <div className="border-top p-3 p-md-4">
              {!puedeRegistrarRevision && (
                <div className="alert alert-light border rounded-4 mb-4">
                  <i className="fa-solid fa-lock me-2"></i>
                  Este expediente se encuentra en modo consulta.
                </div>
              )}

              <div className="d-flex flex-column gap-3">
                {criterios.map(
                  (criterio, index) => {
                    const estado =
                      detalles[criterio._id]
                        ?.estadoCriterioRevision ||
                      "";

                    const estiloCriterio =
                      obtenerEstiloEstado(estado);

                    return (
                      <section
                        key={criterio._id}
                        className="border rounded-4 p-3"
                      >
                        <div className="row g-3 align-items-start">
                          <div className="col-12 col-lg-5">
                            <div className="d-flex align-items-start gap-3">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{
                                  width: 42,
                                  height: 42,
                                  backgroundColor:
                                    estado
                                      ? estiloCriterio.color
                                      : colores.gris,
                                }}
                              >
                                {index + 1}
                              </div>

                              <div>
                                <div className="d-flex flex-wrap gap-2 align-items-center">
                                  <div className="fw-bold">
                                    {
                                      criterio.nombreCriterio
                                    }
                                  </div>

                                  {criterio.obligatorioCriterio !==
                                    false && (
                                    <span className="badge bg-danger-subtle text-danger border rounded-pill">
                                      Obligatorio
                                    </span>
                                  )}
                                </div>

                                {criterio.descripcionCriterio && (
                                  <small className="text-secondary d-block mt-1">
                                    {
                                      criterio.descripcionCriterio
                                    }
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-md-4 col-lg-3">
                            <label className="form-label small fw-semibold">
                              Resultado
                            </label>

                            <select
                              className="form-select rounded-3"
                              disabled={
                                !puedeRegistrarRevision
                              }
                              value={estado}
                              onChange={(e) =>
                                cambiarDetalle(
                                  criterio._id,
                                  "estadoCriterioRevision",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">
                                Seleccione...
                              </option>
                              <option value="Correcto">
                                Correcto
                              </option>
                              <option value="Requiere ajuste">
                                Requiere ajuste
                              </option>
                              <option value="No aplica">
                                No aplica
                              </option>
                            </select>
                          </div>

                          <div className="col-12 col-md-8 col-lg-4">
                            <label className="form-label small fw-semibold">
                              Observación
                            </label>

                            <textarea
                              className="form-control rounded-3"
                              rows="2"
                              disabled={
                                !puedeRegistrarRevision
                              }
                              value={
                                detalles[
                                  criterio._id
                                ]
                                  ?.observacionCriterioRevision ||
                                ""
                              }
                              onChange={(e) =>
                                cambiarDetalle(
                                  criterio._id,
                                  "observacionCriterioRevision",
                                  e.target.value
                                )
                              }
                              placeholder="Observación del criterio..."
                            />
                          </div>
                        </div>
                      </section>
                    );
                  }
                )}
              </div>
            </div>
          )}
        </article>

        {/* =========================================================
            CONCEPTO FINAL
        ========================================================= */}
        {puedeRegistrarRevision && (
          <article className="bg-white border rounded-4 shadow-sm overflow-hidden mb-4">
            <div className="p-3 p-md-4">
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                <div className="d-flex flex-column flex-md-row align-items-center gap-2 text-center text-md-start">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center text-white"
                    style={{
                      width: 38,
                      height: 38,
                      backgroundColor: colores.azul,
                    }}
                  >
                    <i className="fa-solid fa-gavel"></i>
                  </div>

                  <div>
                    <h5
                      className="fw-bold mb-0"
                      style={{
                        color: colores.azul,
                      }}
                    >
                      Concepto final de auditoría
                    </h5>

                    <small className="text-secondary">
                      Registra el resultado y las observaciones finales
                    </small>
                  </div>
                </div>

                <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                  Avance {estadisticas.porcentaje}%
                </span>
              </div>
            </div>

            <div className="border-top p-3 p-md-4">
              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <label className="form-label fw-semibold">
                    Resultado final *
                  </label>

                  <select
                    className="form-select form-select-lg rounded-3"
                    value={resultadoRevision}
                    onChange={(e) =>
                      setResultadoRevision(
                        e.target.value
                      )
                    }
                    required
                  >
                    <option value="">
                      Seleccione...
                    </option>
                    <option value="Aprobado">
                      Aprobado
                    </option>
                    <option value="Requiere ajustes">
                      Requiere ajustes
                    </option>
                    <option value="Rechazado">
                      Rechazado
                    </option>
                  </select>
                </div>

                <div className="col-12 col-lg-8">
                  <label className="form-label fw-semibold">
                    Concepto del auditor
                  </label>

                  <input
                    type="text"
                    className="form-control form-control-lg rounded-3"
                    value={conceptoAuditor}
                    onChange={(e) =>
                      setConceptoAuditor(
                        e.target.value
                      )
                    }
                    placeholder="Concepto general de la revisión..."
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Observación general
                  </label>

                  <textarea
                    className="form-control rounded-3"
                    rows="4"
                    value={observacionRevision}
                    onChange={(e) =>
                      setObservacionRevision(
                        e.target.value
                      )
                    }
                    placeholder="Observaciones generales para el instructor..."
                  />
                </div>
              </div>
            </div>

            <div className="border-top p-3 p-md-4 bg-light">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                <div className="text-center text-md-start">
                  <div className="fw-bold">
                    Finalizar revisión
                  </div>

                  <small className="text-secondary">
                    El resultado quedará registrado en el historial de auditoría.
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-dark rounded-3 px-4 py-2"
                  disabled={
                    guardando ||
                    criterios.length === 0
                  }
                >
                  {guardando ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin me-2"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-circle-check me-2"></i>
                      Finalizar revisión
                    </>
                  )}
                </button>
              </div>
            </div>
          </article>
        )}
      </form>

      {/* =========================================================
          HISTORIAL
      ========================================================= */}
      <article
        id="historialAuditoria"
        className="bg-white border rounded-4 shadow-sm overflow-hidden mb-4"
      >
        <div className="p-3 p-md-4">
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
            <div className="d-flex flex-column flex-md-row align-items-center gap-2 text-center text-md-start">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white"
                style={{
                  width: 38,
                  height: 38,
                  backgroundColor: colores.azul,
                }}
              >
                <i className="fa-solid fa-clock-rotate-left"></i>
              </div>

              <div>
                <h5
                  className="fw-bold mb-0"
                  style={{ color: colores.azul }}
                >
                  Historial de revisiones
                </h5>

                <small className="text-secondary">
                  Trazabilidad del expediente documental
                </small>
              </div>
            </div>

            <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
              {revisiones.length} revisión
              {revisiones.length === 1 ? "" : "es"}
            </span>
          </div>
        </div>

        {revisiones.length === 0 ? (
          <div className="text-center py-5 border-top">
            <i className="fa-solid fa-clock-rotate-left fs-1 text-secondary"></i>

            <h5 className="mt-3">
              Sin revisiones finalizadas
            </h5>

            <p className="text-secondary mb-0">
              Las revisiones aparecerán aquí cuando sean registradas.
            </p>
          </div>
        ) : (
          <div className="table-responsive border-top">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-4 py-3">
                    Revisión
                  </th>
                  <th className="py-3">
                    Fecha
                  </th>
                  <th className="py-3">
                    Auditor
                  </th>
                  <th className="py-3">
                    Resultado
                  </th>
                  <th className="py-3">
                    Concepto
                  </th>
                  <th className="px-4 py-3">
                    Observación
                  </th>
                </tr>
              </thead>

              <tbody>
                {revisiones.map((revision) => {
                  const estilo =
                    obtenerEstiloEstado(
                      revision.resultadoRevision
                    );

                  return (
                    <tr key={revision._id}>
                      <td className="px-4">
                        <span className="badge bg-light text-dark border rounded-pill px-3 py-2">
                          #{revision.numeroRevision}
                        </span>
                      </td>

                      <td className="text-nowrap">
                        <i className="fa-regular fa-calendar me-2 text-primary"></i>
                        {formatearFechaHora(
                          revision.fechaRevision
                        )}
                      </td>

                      <td className="fw-semibold">
                        {nombrePersona(
                          revision.auditorObjectId
                        )}
                      </td>

                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 ${estilo.clase}`}
                        >
                          <i
                            className={`${estilo.icono} me-2`}
                          ></i>

                          {
                            revision.resultadoRevision
                          }
                        </span>
                      </td>

                      <td>
                        {revision.conceptoAuditor ||
                          "—"}
                      </td>

                      <td className="px-4">
                        {revision.observacionRevision ||
                          "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </main>
  );
};

export default Auditoria_RevisarDocumento;